import React from 'react';
import { createPortal } from 'react-dom';
import type { SimpleInOutStatusValue } from '../../api/simpleInOut';
import {
  SIMPLE_INOUT_STATUS_OPTIONS,
  formatSimpleInOutEpoch,
  simpleInOutStatusLabel,
  useMySimpleInOutStatus,
} from './useMySimpleInOutStatus';

function statusButtonClassName(active: boolean): string {
  return ['btn', 'xs', 'simple-inout-status-button', active ? 'primary' : 'btn-outline'].join(' ');
}

/** The full status list and optional comment for the signed-in person. */
export default function SimpleInOutStatusModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sio = useMySimpleInOutStatus();
  const [comment, setComment] = React.useState('');
  const titleId = React.useId();
  const showing = open && sio.loaded;

  React.useEffect(function bindSimpleInOutModalEscape() {
    if (!showing) return undefined;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.body.classList.add('modal-open');
    document.addEventListener('keydown', handleKeyDown);
    return function cleanupSimpleInOutModalEscape() {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [showing, onClose]);

  if (!showing) return null;

  const { currentStatus } = sio;
  const selectableOptions = currentStatus
    ? SIMPLE_INOUT_STATUS_OPTIONS.filter((option) => option.value !== currentStatus)
    : SIMPLE_INOUT_STATUS_OPTIONS;

  function handleStatus(status: SimpleInOutStatusValue) {
    sio.setStatus(status, comment);
  }

  return createPortal(
    <div
      className="modal-overlay simple-inout-status-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="modal simple-inout-status-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header simple-inout-status-modal__header">
          <div className="stack gap-xxs">
            <span className="text-xs fg-muted">Simple I/O</span>
            <h2 id={titleId} className="modal__title">My status</h2>
          </div>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Close Simple I/O status modal">
            x
          </button>
        </div>

        <div className="modal-body simple-inout-status-modal__body">
          <div className="simple-inout-status-modal__current">
            <span className="simple-inout-status-pill" data-status={currentStatus || 'unknown'}>
              {simpleInOutStatusLabel(currentStatus)}
            </span>
            <div className="simple-inout-status-card__meta">
              <div>
                <span>Comment</span>
                <strong>{sio.comment || '--'}</strong>
              </div>
              <div>
                <span>Last updated</span>
                <strong>{formatSimpleInOutEpoch(sio.updatedAt)}</strong>
              </div>
            </div>
          </div>

          <div className="simple-inout-status-card__controls simple-inout-status-modal__controls" role="group" aria-label="Update Simple I/O status">
            {selectableOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={statusButtonClassName(currentStatus === option.value)}
                data-status={option.value}
                data-active={currentStatus === option.value ? 'true' : undefined}
                aria-pressed={currentStatus === option.value}
                disabled={sio.pending || sio.loading}
                onClick={() => handleStatus(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <label className="settings-field simple-inout-status-card__comment">
            <span className="settings-field__label">Optional comment</span>
            <input
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 50))}
              placeholder="At warehouse, on site, lunch..."
              maxLength={50}
            />
          </label>
        </div>
      </div>
    </div>,
    document.body,
  );
}
